"use client";

import { useQuery } from "@tanstack/react-query";
import { getMyStickerStats, getProfileStickers } from "@/actions/stickers";
import { useAuthState } from "@/hooks/use-auth-state";
import { stickerKeys } from "@/lib/query-keys/stickers";

export { stickerKeys };

const STALE_TIME_MS = 60_000;

/**
 * The signed-in member's own sticker counters — what they may share, and how
 * close they are on someone else's. `null` when signed out.
 */
export function useMyStickers(enabled = true) {
  const { authStatus } = useAuthState();
  const signedIn = authStatus === "authenticated";
  const query = useQuery({
    queryKey: stickerKeys.mine,
    queryFn: async () => {
      const result = await getMyStickerStats();
      if (!result.ok) throw new Error(result.message);
      return result.data;
    },
    enabled: enabled && signedIn,
    staleTime: STALE_TIME_MS,
  });
  return {
    ...query,
    signedIn,
    authResolved: authStatus !== "unknown",
    data: signedIn ? (query.data ?? null) : null,
  };
}

/**
 * A profile's sticker row: the owner's ids and the viewer's, in one call that
 * starts at once (the server knows who is asking — no wait on auth state).
 */
export function useProfileStickers(userId: string) {
  return useQuery({
    queryKey: stickerKeys.profile(userId),
    queryFn: async () => {
      const result = await getProfileStickers(userId);
      if (!result.ok) throw new Error(result.message);
      return result.data;
    },
    staleTime: STALE_TIME_MS,
  });
}
